/* eslint-disable jsx-a11y/anchor-is-valid */
import {Autocomplete, TextField} from '@mui/material'
import {FUNDING, PayPalButtons, PayPalScriptProvider} from '@paypal/react-paypal-js'
import {skipToken} from '@reduxjs/toolkit/query'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {Spinner} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {CashAppPay, CreditCard, PaymentForm} from 'react-square-web-payments-sdk'
import * as Yup from 'yup'
import {INTEGER_FIELD, REQUIRED_FIELD} from '../../../lib/constants'
import {
  useGetOrganizationInfoQuery,
  useOrganizationListQuery,
  useRegisterCustomerMutation,
  useWalletSalesPersonQuery,
} from '../../api/backendApi'

const ERRORS: Record<string, string> = {
  CVV_FAILURE: 'Card CVV incorrect',
  ADDRESS_VERIFICATION_FAILURE: 'Card postal code incorrect',
  INVALID_EXPIRATION: 'Card expiration date incorrect',
  GENERIC_DECLINE: 'Card declined number',
} as const

const autocompleteSx = {
  '& .MuiInputBase-root': {
    p: '0.775rem 43px 0.775rem 1rem',
    borderRadius: '0.475rem',
  },

  '& .MuiOutlinedInput-root .MuiInputBase-input': {
    p: 0,
    fontSize: '1.1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    height: 'unset',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}

const registrationSchema = Yup.object().shape({
  email: Yup.string()
    .required(REQUIRED_FIELD)
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email is wrong')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  phone: Yup.string()
    .required(REQUIRED_FIELD)
    .matches(/^[0-9]+$/, 'Must be only digits')
    // .matches(
    //   /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|\+?([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
    //   'Phone number is not valid'
    // )
    .min(10, 'Must be exactly 10 digits')
    .max(10, 'Must be exactly 10 digits'),
  firstName: Yup.string().required(REQUIRED_FIELD),
  lastName: Yup.string().required(REQUIRED_FIELD),
  zipCode: Yup.string()
    .required(REQUIRED_FIELD)
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(5, 'Must be exactly 5 digits')
    .max(5, 'Must be exactly 5 digits'),
  organizationName: Yup.string().required(REQUIRED_FIELD),
  salesPerson: Yup.string().required(REQUIRED_FIELD),
  amount: Yup.string()
    .required(REQUIRED_FIELD)
    .matches(/^[0-9]+$/, INTEGER_FIELD)
    .test('test-min-value', 'Should not be less than $20', (value) => +value >= 20),
})

type FormValues = {
  email: string
  phone: string
  firstName: string
  lastName: string
  packet: string
  salesPerson: string
  organizationName: string
  organizationId: string
  zipCode: string
  amount: string
}

const initialValues: FormValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  packet: '',
  zipCode: '',
  salesPerson: '',
  organizationName: '',
  organizationId: '',
  amount: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

const handleError = (message: string) => {
  const errorMessage = Object.entries(ERRORS).find(([key, value]) => message.includes(key))?.[1]

  return errorMessage ?? message
}

export function DigitalCard() {
  const [stage, setStage] = useState<'INFO' | 'PAYMENT' | 'ORGANISATION'>('ORGANISATION')
  const [loading, setLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<string>()

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    validateOnMount: true,
    onSubmit: () => undefined,
  })

  const [register, {isSuccess, data, isLoading: registering}] = useRegisterCustomerMutation()

  const {data: organizations} = useOrganizationListQuery({})
  const {value: organizationId} = formik.getFieldProps('organizationId')
  const {data: organization} = useGetOrganizationInfoQuery(
    organizationId ? {walletId: organizationId} : skipToken
  )

  const amounts = useMemo(() => {
    return organization?.amounts || []
  }, [organization?.amounts])

  const {data: sellers} = useWalletSalesPersonQuery(
    organizationId ? {walletId: organizationId} : skipToken
  )

  const organizationsOptions = useMemo(
    () =>
      organizations?.map((o) => ({
        label: o.name,
        id: o.id,
      })),
    [organizations]
  )

  const sellersOptions = useMemo(
    () =>
      sellers?.map(({name, packet}) => ({
        label: name,
        packet,
      })),
    [sellers]
  )

  const baseAmount = useMemo(() => {
    return +(formik.values.amount || 0)
  }, [formik.values.amount])

  const processingFeeAmount = useMemo(() => {
    const processingFeePercent = +(process.env.REACT_APP_PROCESSING_FEE || 0) / 100
    return baseAmount * processingFeePercent
  }, [baseAmount])

  const totalAmount = useMemo(() => {
    return baseAmount + processingFeeAmount
  }, [baseAmount, processingFeeAmount])

  const registerPayment = useCallback(
    (paymentToken?: string, paymentProvider?: string) => {
      console.log('registerPayment', paymentToken, paymentProvider)
      console.log('formik.values', formik.values)
      if (!registering && paymentToken) {
        setLoading(true)

        register({
          walletId: formik.values.organizationId,
          registerCustomerRequest: {
            firstName: formik.values.firstName,
            lastName: formik.values.lastName,
            telephone: formik.values.phone,
            email: formik.values.email,
            salesPerson: formik.values.salesPerson ?? undefined,
            packet: formik.values.packet ?? undefined,
            zipCode: formik.values.zipCode,
            paymentToken,
            paymentProvider,
            amount: +(formik.values.amount || 0),
          },
        })
          .unwrap()
          .then(() => {
            console.log('Registered')
          })
          .catch((e) => {
            console.log('Register error')

            setPaymentError(
              'data' in e && 'message' in e.data && typeof e.data.message == 'string'
                ? handleError(e.data.message)
                : 'Something wrong'
            )
          })
          .finally(() => setLoading(false))
      }
    },
    [formik.values, register, registering]
  )

  useEffect(() => {
    const savedForm = localStorage.getItem('digitalCardForm')
    const params = new URLSearchParams(window.location.search)
    const cashRequestId = params.get('cash_request_id')

    console.log('savedForm', savedForm)
    console.log('cashRequestId', cashRequestId)

    if (savedForm && cashRequestId) {
      const formData = JSON.parse(savedForm)
      formik.setValues(formData, false).then(() => setStage('PAYMENT'))
      setLoading(true)
    }

    localStorage.removeItem('digitalCardForm')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isSuccess && data ? (
    <div className='col'>
      {organization?.logoImageUrl && (
        <div className='mb-4 d-flex justify-content-center'>
          <img width='100%' src={organization.logoImageUrl} alt={organization.name} />
        </div>
      )}
      <div className='text-center mb-11'>
        <h3 className='text-dark fw-bolder'>{data.successMessage}</h3>
      </div>
      <div className='text-center mb-11'>
        <h3 className='text-dark fw-bolder'>
          Thank you for your support and donation. Your payment has been received and you are now
          able to download your digital savings pass. Please look for a confirmation email with the
          link to download your pass to your Apple or Google wallet. If you do not see the email in
          your inbox, please look in your SPAM folder as well. Make sure you click on the link from
          your phone and not a computer
        </h3>
      </div>
      <div className='mb-4 mt-10 d-flex justify-content-center'>
        <img width='200px' src='/media/logos/logo.png' alt='' />
      </div>
    </div>
  ) : (
    <div>
      {loading && (
        <div
          className='d-flex justify-content-center align-items-center position-fixed top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-75'
          style={{zIndex: 1000}}
        >
          <Spinner animation='border' role='status'></Spinner>
        </div>
      )}
      {stage === 'ORGANISATION' || !organization ? (
    <div className='d-flex flex-column'>
      <div className='fv-row mb-3'>
            <div className='small mb-10'>
              Thank you for your interest in supporting the youth in your community. From the
              dropdown below, please select which organization you are supporting.
            </div>

        <Autocomplete
          sx={{
            ...autocompleteSx,
            '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
              ...(formik.touched.organizationName ? {right: 32} : {}),
            },
            '& .MuiOutlinedInput-root': {
              ...(formik.touched.organizationName ? {pr: '70px !important'} : {}),
            },
          }}
          disablePortal
          isOptionEqualToValue={(option, value) => {
            return option.label === value
          }}
          options={organizationsOptions ?? []}
          {...formik.getFieldProps('organizationName')}
          onChange={(e, selectedObject) => {
            if (selectedObject !== null) {
              if (organizationId !== selectedObject.id) {
                formik.setFieldValue('organizationName', selectedObject.label)
                formik.setFieldValue('organizationId', selectedObject.id)
                formik.setFieldValue('salesPerson', '')
                formik.setFieldValue('packet', '')
              }
              setStage('INFO')
            } else {
              formik.setFieldValue('organizationName', '')
              formik.setFieldValue('organizationId', '')
              formik.setFieldValue('salesPerson', '')
              formik.setFieldValue('packet', '')
            }

            formik
              .setFieldTouched('organizationName')
              .finally(() => formik.validateField('organizationName'))
          }}
          renderInput={({InputProps, ...params}) => (
            <TextField
              {...params}
              InputProps={{
                ...InputProps,
                className: clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid':
                          formik.touched.organizationName && formik.errors.organizationName,
                      },
                      {
                        'is-valid':
                          formik.touched.organizationName && !formik.errors.organizationName,
                      }
                    ),
              }}
              type='text'
              placeholder='Organization Name'
            />
          )}
        />

        {formik.touched.organizationName && formik.errors.organizationName && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.organizationName}</span>
            </div>
          </div>
        )}
        <div className='mb-4 mt-10 d-flex justify-content-center'>
          <img width='200px' src='/media/logos/logo.png' alt='' />
        </div>
      </div>
    </div>
  ) : stage === 'INFO' ? (
    <form
      className={`form w-100 ${stage === 'INFO' ? '' : 'd-none'}`}
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <Link
        style={{fontSize: 16, marginBottom: '21px', display: 'flex'}}
        to=''
        onClick={() => {
          setStage('ORGANISATION')
          setPaymentError(undefined)
        }}
      >
        {'<< Back'}
      </Link>
      {/* begin::Heading */}

      {/* begin::Heading */}

      {formik.status ? (
        <div className='mb-10 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : null}

      {organization?.logoImageUrl && (
        <div className='mb-4 d-flex justify-content-center'>
          <img width='100%' src={organization.logoImageUrl} alt={organization.name} />
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
        <label className='form-label fs-6 fw-bolder text-dark required'>First Name</label>
        <input
          placeholder='First Name'
          {...formik.getFieldProps('firstName')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.firstName && formik.errors.firstName},
            {
              'is-valid': formik.touched.firstName && !formik.errors.firstName,
            }
          )}
          type='name'
          name='firstName'
          autoComplete='off'
        />
        {formik.touched.firstName && formik.errors.firstName && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.firstName}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
        <label className='form-label fs-6 fw-bolder text-dark required'>Last Name</label>
        <input
          placeholder='Last Name'
          {...formik.getFieldProps('lastName')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.lastName && formik.errors.lastName},
            {
              'is-valid': formik.touched.lastName && !formik.errors.lastName,
            }
          )}
          type='name'
          name='lastName'
          autoComplete='off'
        />
        {formik.touched.lastName && formik.errors.lastName && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.lastName}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
        <label className='form-label fs-6 fw-bolder text-dark required'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0 required'>Phone</label>
        <input
          type='phone'
          placeholder='Phone'
          autoComplete='off'
          {...formik.getFieldProps('phone')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.phone && formik.errors.phone,
            },
            {
              'is-valid': formik.touched.phone && !formik.errors.phone,
            }
          )}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.phone}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-4'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0 required'>Zip Code</label>
        <input
          type='text'
          placeholder='Zip Code'
          autoComplete='off'
          {...formik.getFieldProps('zipCode')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.zipCode && formik.errors.zipCode,
            },
            {
              'is-valid': formik.touched.zipCode && !formik.errors.zipCode,
            }
          )}
        />
        {formik.touched.zipCode && formik.errors.zipCode && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.zipCode}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0 required'>
          Participants Name
        </label>
        <Autocomplete
          sx={{
            ...autocompleteSx,
            '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
              ...(formik.touched.salesPerson ? {right: 32} : {}),
            },
            '& .MuiOutlinedInput-root': {
              ...(formik.touched.salesPerson ? {pr: '70px !important'} : {}),
            },
          }}
          disablePortal
          isOptionEqualToValue={(option, value) => {
            return option.label === value
          }}
          options={sellersOptions ?? []}
          {...formik.getFieldProps('salesPerson')}
          onChange={(e, selectedObject) => {
            if (selectedObject !== null) {
              formik.setFieldValue('salesPerson', selectedObject.label)
              formik.setFieldValue('packet', selectedObject.packet)
            } else {
              formik.setFieldValue('salesPerson', '')
              formik.setFieldValue('packet', '')
                }

                formik
                  .setFieldTouched('salesPerson')
                  .finally(() => formik.validateField('salesPerson'))
              }}
          renderInput={({InputProps, ...params}) => (
            <TextField
              {...params}
              InputProps={{
                ...InputProps,
                className: clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.salesPerson && formik.errors.salesPerson,
                  },
                  {
                    'is-valid': formik.touched.salesPerson && !formik.errors.salesPerson,
                  }
                ),
              }}
              type='text'
              placeholder='Participants Name'
            />
          )}
        />

        {formik.touched.salesPerson && formik.errors.salesPerson && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.salesPerson}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0 required'>Amount</label>
        <Autocomplete
          sx={{
            '& .MuiInputBase-root': {
              p: '0.775rem 43px 0.775rem 1rem',
              borderRadius: '0.475rem',
            },

            '& .MuiOutlinedInput-root .MuiInputBase-input': {
              p: 0,
              fontSize: '1.1rem',
              fontWeight: 500,
              lineHeight: 1.5,
              height: 'unset',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
              ...(formik.touched.amount ? {right: 32} : {}),
            },
            '& .MuiOutlinedInput-root': {
              ...(formik.touched.amount ? {pr: '70px !important'} : {}),
            },
          }}
          disablePortal
          freeSolo
          autoSelect
          // isOptionEqualToValue={(option, value) => {
          //   return option.label === value
          // }}
          options={amounts.map((a) => String(a))}
          {...formik.getFieldProps('amount')}
          onChange={(e, selectedObject) => {
            formik.setFieldValue('amount', selectedObject)
            formik.setFieldTouched('amount').finally(() => formik.validateField('amount'))
          }}
          // onInput={(e) => {
          //   formik.setFieldValue('amount', e.target.value)
          //   // formik.setFieldTouched('amount').finally(() => formik.validateField('amount'))
          // }}
          renderInput={({InputProps, ...params}) => (
            <TextField
              {...params}
              InputProps={{
                ...InputProps,
                className: clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.amount && formik.errors.amount,
                  },
                  {
                    'is-valid': formik.touched.amount && !formik.errors.amount,
                  }
                ),
              }}
              type='text'
              placeholder='Amount'
            />
          )}
        />
        {formik.touched.amount && formik.errors.amount && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.amount}</span>
            </div>
          </div>
        )}
      </div>

      {/* begin::Action */}
      <div className='d-grid mb-10 gap-3'>
        <button
          id='kt_sign_in_submit'
          className='btn btn-primary'
          onClick={() => {
            formik.validateForm().then((errors) => {
              if (Object.keys(errors).length === 0) {
                // Save all formik values to local storage
                localStorage.setItem('digitalCardForm', JSON.stringify(formik.values))
                setStage('PAYMENT')
              }
            })
          }}
        >
          {!loading && <span className='indicator-label'>Go To Process Donation</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      <div className='mb-4 d-flex justify-content-center'>
        <img width='200px' src='/media/logos/logo.png' alt='' />
      </div>

      {/* end::Action */}
    </form>
  ) : (
        <div className={`d-flex flex-column ${stage === 'PAYMENT' ? '' : 'd-none'}`}>
      <Link
        style={{fontSize: 16, marginBottom: '21px'}}
        to=''
        onClick={() => {
          setStage('INFO')
          setPaymentError(undefined)
        }}
      >
        {'<< Back'}
      </Link>
      {organization?.logoImageUrl && (
        <div className='mb-4 d-flex justify-content-center'>
          <img width='100%' src={organization.logoImageUrl} alt={organization.name} />
        </div>
      )}
          <div className='mb-4 d-flex flex-column gap-3 align-items-stretch'>
        <PaymentForm
          applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID || ''}
          cardTokenizeResponseReceived={(token, verifiedBuyer) => {
            console.log('Cash App Pay token received:', token)
            console.log('Cash App Pay verifiedBuyer:', verifiedBuyer)
            // Для CashAppPay токен должен приходить через этот callback
            if (token && token.token) {
              registerPayment(token.token, 'square')
            }
          }}
          createPaymentRequest={() => {
            const request = {
              countryCode: 'US',
              currencyCode: 'USD',
              lineItems: [
                {
                  amount: baseAmount.toFixed(2),
                  label: formik.values.organizationName || 'Donation',
                  id: formik.values.organizationId || undefined,
                },
              ],
              taxLineItems:
                processingFeeAmount > 0
                  ? [
                      {
                        amount: processingFeeAmount.toFixed(2),
                        label: 'Processing Fee',
                      },
                    ]
                  : undefined,
              total: {
                amount: totalAmount.toFixed(2),
                label: formik.values.organizationName || 'Total',
              },
              shippingContact: {
                givenName: formik.values.firstName,
                familyName: formik.values.lastName,
                email: formik.values.email,
                phone: formik.values.phone,
                postalCode: formik.values.zipCode,
                countryCode: 'US',
              },
              requestBillingContact: true,
            }

            console.log('Cash App Pay Request:', request)
            return request
          }}
          locationId={process.env.REACT_APP_SQUARE_LOCATION_ID || ''}
          key='cash-app-pay'
        >
          <CashAppPay
            size='medium'
            width='full'
            shape='semiround'
            callbacks={{
              onTokenization: (event) => {
                console.log('Cash App Pay tokenization:', event)
                // Для CashAppPay токен должен приходить через этот callback
                if (event.detail.tokenResult?.token) {
                  registerPayment(event.detail.tokenResult.token, 'square')
                }
              },
            }}
          />
        </PaymentForm>
        <PayPalScriptProvider
          options={{
            clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
            enableFunding: 'venmo',
            currency: 'USD',
            ...(process.env.REACT_APP_PAYPAL_MODE === 'sandbox' ? {buyerCountry: 'US'} : {}),
          }}
        >
          <PayPalButtons
            style={{layout: 'vertical', disableMaxWidth: true}}
            fundingSource={FUNDING.VENMO}
            createOrder={(_, actions) => {
              const paypalRequest = {
                intent: 'CAPTURE' as const,
                purchase_units: [
                  {
                    amount: {
                      currency_code: 'USD',
                      value: totalAmount.toFixed(2),
                      breakdown: {
                        item_total: {
                          currency_code: 'USD',
                          value: baseAmount.toFixed(2),
                        },
                        // The processing fee is a handling charge, not sales tax. It was
                        // previously sent as tax_total, which PayPal prints as "Tax" on the
                        // donor's receipt - asserting tax was collected and remitted when it
                        // was not, and creating a bookkeeping problem. handling renders as a
                        // processing charge and carries no tax implication. The amount is
                        // unchanged: item_total + handling still equals the total charged.
                        ...(processingFeeAmount > 0
                          ? {
                              handling: {
                                currency_code: 'USD',
                                value: processingFeeAmount.toFixed(2),
                              },
                            }
                          : {}),
                      },
                    },
                    items: [
                      {
                        name: formik.values.organizationName || 'Donation',
                        unit_amount: {
                          currency_code: 'USD',
                          value: baseAmount.toFixed(2),
                        },
                        quantity: '1',
                      },
                    ],
                    description: formik.values.organizationName || 'Donation',
                  },
                ],
                payer: {
                  name: {
                    given_name: formik.values.firstName,
                    surname: formik.values.lastName,
                  },
                  email_address: formik.values.email,
                  phone: {
                    phone_type: 'MOBILE' as const,
                    phone_number: {
                      country_code: '1',
                      national_number: formik.values.phone,
                    },
                  },
                  address: {
                    postal_code: formik.values.zipCode,
                    country_code: 'US',
                  },
                },
              }

              console.log('PayPal Create Order Request:', paypalRequest)

              return actions.order.create(paypalRequest)
            }}
            onApprove={(data) => {
              console.log('PayPal payment approved:', data)
              if (data.orderID) {
                registerPayment(data.orderID, 'paypal')
              }
              return Promise.resolve()
            }}
            onError={(err) => {
              console.error('PayPal error:', err)
              setPaymentError('PayPal payment error. Please try again.')
            }}
            onCancel={(data) => {
              console.log('PayPal payment cancelled:', data)
            }}
          />
        </PayPalScriptProvider>
      </div>

      <PaymentForm
        applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID || ''}
        cardTokenizeResponseReceived={(token, verifiedBuyer) => {
          console.log('token:', token)
          console.log('verifiedBuyer:', verifiedBuyer)
          registerPayment(token.token, 'square')
        }}
        locationId={process.env.REACT_APP_SQUARE_LOCATION_ID || ''}
        key='credit-card'
      >
        <CreditCard
          postalCode={formik.values.zipCode}
          style={{
            input: {
              fontSize: '14.3px',
              // fontFamily: 'Inter, Helvetica',
              fontWeight: '500',
            },
          }}
          buttonProps={{
            className: 'btn btn-primary',
            isLoading: loading,
          }}
        >
          {`Total Due $${totalAmount}`}
        </CreditCard>
      </PaymentForm>
      <div style={{marginTop: '5px'}} className='small'>
        {`Please note up to a ${
          process.env.REACT_APP_PROCESSING_FEE || 0
        }% fee will be charged to cover processing fees. This will allow the organization to maximize their earnings. Thank you for taking time to support the youth in your local community.`}
      </div>
      {paymentError ? (
        <div className='alert alert-danger' style={{marginTop: '10px'}}>
          <div className='alert-text font-weight-bold'>{paymentError}</div>
        </div>
      ) : null}
      <div className='mb-4 mt-10 d-flex justify-content-center'>
        <img width='200px' src='/media/logos/logo.png' alt='' />
          </div>
        </div>
      )}
    </div>
  )
}
